import pick from 'lodash/pick';
import trimHtml from 'trim-html';

import { ILocationV2 } from '@sravni/types/lib/locations';

import { LOCATION_ITEM_FIELDS } from '../constants/locations';
import { ORGANIZATION_ITEM_FIELDS } from '../constants/organizations';
import {
  IItem as IOrganizationItem,
  InsuranceServiceItem,
  OrganizationServiceItem,
  OrganizationType,
} from '../interfaces/organizations';
import {
  DecoratedTagsByOrganizationType,
  ICommentsList,
  ICompactReview,
  ICompactReviewList,
  IDetailedReview,
  IFiltersList,
  IReviewFilters,
  IReviewItem,
  IReviewListItem,
  IVotesByReviewId,
  TAllComments,
} from '../interfaces/reviews';
import { putDecoratedTagsToCache } from '../server/controllers/products';
import { render } from '../utils/markdown';
import { handTypograf } from '../utils/typograf';

const MAX_TEXT_LENGTH = 350;

interface IDecorateReviewsParams {
  reviews: ICompactReviewList;
  organizations?: IOrganizationItem[];
  organization?: IOrganizationItem;
  votes?: IVotesByReviewId;
}

type DecorateReviewsReturnedType = {
  items: IReviewListItem[];
  total: number;
  pageSize: number;
};

export const decorateCommentsList = (list: ICommentsList): TAllComments => {
  if (!list?.items)
    return {
      ...list,
      items: [],
    };

  const items = list.items.map((comment) => ({ ...comment, html: render(comment.text) }));

  return {
    ...list,
    items,
  };
};

export const decorateFiltersList = (list: IFiltersList): IFiltersList => {
  if (list?.tag?.length) {
    return {
      ...list,
      tag: [{ key: 'All', name: 'Все услуги', alias: 'All' }, ...list.tag],
    };
  }

  return list;
};

export const normalizeInsuranceCompany = (company: InsuranceServiceItem): IOrganizationItem =>
  (pick(
    {
      ...company,
      type: OrganizationType.INSURANCE,
      status: company.status.toLowerCase(),
      isInsurance: true, // временно, что бы не падал старый код, нужно ориентироваться на type
      logo: `https://f.sravni.ru/logotypes/ic/biglogo_${company.id}.svg`,
      roundLogo: `https://f.sravni.ru/logotypes/ic/40x40/logo_${company.id}.svg`,
      isRecomended: company.isRecomended,
    },
    ORGANIZATION_ITEM_FIELDS,
  ) as unknown) as IOrganizationItem;

export const normalizeOrganization = (company: OrganizationServiceItem): IOrganizationItem => {
  if (!company) {
    return (company as unknown) as IOrganizationItem;
  }
  return pick(
    {
      ...company,
      id: company._id,
      fullName: company.name?.full,
      prepositionalName: company.name?.prepositional,
      name: company.name?.short,
      logo: company.logotypes?.web,
      roundLogo: company.logotypes?.android,
      squareLogo: company.logotypes?.square,
      license: company.license,
      partnerCard: company?.partnerCard,
      ...(company.type === OrganizationType.BANK && { isBank: true }), // временно, что бы не падал старый код, нужно ориентироваться на type
      ...(company.type === OrganizationType.MFO && { isMfo: true }),
    },
    ORGANIZATION_ITEM_FIELDS,
  ) as IOrganizationItem;
};

export const decorateReview = (
  review: IDetailedReview | ICompactReview,
  organization: IOrganizationItem,
): IReviewItem | IReviewListItem => {
  let output: IReviewItem | IReviewListItem = {
    ...review,
    text: handTypograf(review.text),
    title: handTypograf(review.title),
    vote: null,
    votes: { positive: 0, negative: 0 },
  };

  if (organization) {
    const { name, alias } = organization;
    output.organizationName = name || '';
    output.alias = alias || '';
    output.logo = {
      big: organization.logo,
      round: organization.roundLogo,
      square: organization.squareLogo,
    };
  }

  if (!output.authorName) {
    output.authorName = 'Аноним';
  }

  return output;
};

export const decorateReviews = ({
  reviews,
  organizations,
  organization,
  votes,
}: IDecorateReviewsParams): DecorateReviewsReturnedType => {
  const decoratedReviews = {} as DecorateReviewsReturnedType;

  decoratedReviews.items = reviews.items.map((review) => {
    const currentOrganization = organization?.id
      ? organization
      : organizations?.find(({ id }) => id.toString() === review.reviewObjectId.toString());

    if (!currentOrganization) return review;

    const decoratedItem = decorateReview(review, currentOrganization);

    const { more, html } = trimHtml(render(decoratedItem.text), { suffix: '... ', limit: MAX_TEXT_LENGTH });

    decoratedItem.text = render(decoratedItem.text);
    decoratedItem.html = html;
    decoratedItem.moreHtml = more;

    if (votes) {
      const voteForReview = votes?.[review.id];

      decoratedItem.vote = voteForReview?.userVote ?? null;
      decoratedItem.votes = voteForReview?.votes ?? { positive: 0, negative: 0 };
    }

    return decoratedItem;
  });
  decoratedReviews.pageSize = reviews.pageSize;
  decoratedReviews.total = reviews.total;

  return decoratedReviews;
};

export const decorateReviewsFilters = (
  allFilters: { [key in OrganizationType]?: IReviewFilters },
  vitrinsTagsConfig?,
) => {
  const allProducts = {};

  const decoratedTagsByOrganizationType: DecoratedTagsByOrganizationType = {};

  const decoratedFilters = Object.entries(allFilters).reduce((acc, [organizationType, item]) => {
    const decoratedTags = item.tag.map((tag) => {
      allProducts[organizationType] = [...(allProducts?.[organizationType] ?? []), tag.alias];

      const vitrinsTag = vitrinsTagsConfig?.[tag.alias];
      if (!vitrinsTag || tag.key === 'savings') return tag;

      return {
        ...tag,
        vitrinsProductName: vitrinsTag.productName,
      };
    });

    decoratedTagsByOrganizationType[organizationType] = decoratedTags;

    return {
      ...acc,
      [organizationType]: {
        ...item,
        tag: decoratedTags,
      },
    };
  }, {});

  putDecoratedTagsToCache(decoratedTagsByOrganizationType);

  return { allFilters: decoratedFilters, allProducts };
};

export const normalizeLocation = (location: ILocationV2): Partial<ILocationV2> => pick(location, LOCATION_ITEM_FIELDS);

export const normalizeLocations = (locations: ILocationV2[]): Partial<ILocationV2>[] => {
  return locations.map((location) => normalizeLocation(location));
};
